class HolidayLoadMore extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.grid = this.querySelector('.holiday-shop__products');
    this.button = this.querySelector('[data-load-more]');
    this.status = this.querySelector('[data-load-status]');
    if (!this.grid || !this.button || !this.dataset.collectionUrl) return;
    this.initialized = true;
    this.grid.id = `${this.id}-products`;
    this.batchSize = Number(this.dataset.batchSize) || 8;
    this.seen = new Set([...this.grid.querySelectorAll('[data-holiday-product-id]')].map(card => card.dataset.holidayProductId));
    this.shown = this.seen.size;
    this.pending = [];
    this.nextUrl = this.pageUrl(this.dataset.collectionUrl);
    this.handleClick = () => this.loadMore();
    this.button.addEventListener('click', this.handleClick);
    this.button.hidden = !this.nextUrl;
    this.status.textContent = `${this.shown} products shown`;
  }

  pageUrl(href) {
    if (!href) return null;
    const url = new URL(href, window.location.origin);
    const source = new URL(this.dataset.collectionUrl, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname !== source.pathname) throw new Error('Invalid holiday product page');
    url.searchParams.set('section_id', 'holiday-product-page');
    url.searchParams.set('filter.v.availability', '1');
    return url.pathname + url.search;
  }

  async readPage(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error('Unable to load holiday products');
    const html = await response.text();
    if (/Liquid error\s*\(/i.test(html)) throw new Error('Invalid holiday product response');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const page = doc.querySelector('[data-holiday-product-page]');
    if (!page) throw new Error('Missing holiday product page');
    return {
      cards: [...page.querySelectorAll('[data-holiday-product-id]')],
      next: this.pageUrl(page.dataset.nextUrl),
    };
  }

  async loadMore() {
    if (this.loading || (!this.nextUrl && !this.pending.length)) return;
    this.loading = true;
    this.button.disabled = true;
    this.button.textContent = 'Loading…';
    this.grid.setAttribute('aria-busy', 'true');
    this.status.textContent = 'Loading more products…';
    const controller = new AbortController();
    this.controller = controller;
    try {
      // Skip overlaps with the handpicked products and any empty/filtered pages.
      while (this.pending.length < this.batchSize && this.nextUrl) {
        const currentUrl = this.nextUrl;
        const page = await this.readPage(currentUrl, controller.signal);
        if (controller.signal.aborted || !this.isConnected) return;
        if (page.next === currentUrl) throw new Error('Product pagination did not advance');
        for (const card of page.cards) {
          const id = card.dataset.holidayProductId;
          if (!id || this.seen.has(id)) continue;
          this.seen.add(id);
          this.pending.push(card);
        }
        this.nextUrl = page.next;
      }
      const cards = this.pending.splice(0, this.batchSize);
      this.appendCards(cards);
      this.shown += cards.length;
      this.button.hidden = !this.nextUrl && !this.pending.length;
      this.status.textContent = cards.length
        ? `${cards.length} more products loaded. ${this.shown} products shown.${this.button.hidden ? ' You’ve reached the end of these picks.' : ''}`
        : 'You’ve reached the end of these picks.';
    } catch (error) {
      if (error.name !== 'AbortError') this.status.textContent = 'Products couldn’t load. Please try again.';
    } finally {
      this.loading = false;
      this.button.disabled = false;
      this.button.textContent = 'Load more products';
      this.grid.removeAttribute('aria-busy');
      if (this.controller === controller) this.controller = null;
    }
  }

  appendCards(cards) {
    const fragment = document.createDocumentFragment();
    for (const source of cards) {
      const card = document.adoptNode(source);
      // A product can occur in both collections. Keep form IDs and option-panel
      // references unique to the receiving list, before custom elements connect.
      const ids = new Map();
      card.querySelectorAll('[id]').forEach(node => {
        // A form's input[name="id"] shadows its .id property.
        const oldId = node.getAttribute('id');
        const newId = `${this.id}-${oldId}`;
        node.setAttribute('id', newId);
        ids.set(oldId, newId);
      });
      card.querySelectorAll('[for], [form], [aria-controls], [aria-labelledby], [aria-describedby], a[href^="#"]').forEach(node => {
        for (const attr of ['for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby']) {
          if (node.hasAttribute(attr)) node.setAttribute(attr, node.getAttribute(attr).split(/\s+/).map(id => ids.get(id) || id).join(' '));
        }
        const href = node.getAttribute('href');
        if (href?.startsWith('#') && ids.has(href.slice(1))) node.setAttribute('href', `#${ids.get(href.slice(1))}`);
      });
      fragment.append(card);
    }
    this.grid.append(fragment);
    if (cards.length) {
      const firstAdded = this.grid.children[this.grid.children.length - cards.length];
      firstAdded?.querySelector('.product-grid--title a')?.focus({ preventScroll: true });
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.button?.removeEventListener('click', this.handleClick);
    this.initialized = false;
  }
}

if (!customElements.get('holiday-load-more')) customElements.define('holiday-load-more', HolidayLoadMore);
